import { action, computed, makeObservable, observable } from 'mobx'
import { dependsOn, isPending, trackedAction } from 'mobx-async'

import arriereBackoffice from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import { handleStoreError } from '@new/__global/utils/handleStoreError'
import { BookingPaymentView } from '@new/domains/bookings/_types'
import { BookingAdjustmentStore } from '@new/domains/bookings/stores/BookingAdjustmentStore'
import { BookingStore } from '@new/domains/bookings/stores/BookingStore'
import { BookingValueAddedServicesStore } from '@new/domains/bookings/stores/BookingValueAddedServicesStore'
import { RefundStore } from '@new/domains/bookings/stores/RefundStore'

import { arriere } from '@arriere/index'
import bookings from '@arriere/modules/bookings'
import folio from '@arriere/modules/folio'
import {
  completeDeferredBookingPayment,
  deleteSavedCreditCard,
  getAvailableBookingPaymentCards,
  getAvailablePaymentBanks,
  getAvailablePaymentTerminals,
  getBookingPayments,
  retryPaymentOnPaymentTerminal,
  saveCreditCardFromTerminal,
  setDeferredBookingPaymentAmount,
} from '@arriere/modules/payments'
import {
  CompleteDeferredBookingPaymentProps,
  RetryPaymentProps,
  SetDeferredBookingPaymentAmountProps,
} from '@arriere/types/Payments.types'
import { trackError, trackEvent } from '@tracking/customEventReport'

import { sortPaymentsByRecentActivity } from '../paymentUtils'

class _BookingPaymentStore {
  forceUpdateBookingPaymentsCounter = 0

  forceUpdateBookingCreditCardsCounter = 0

  paymentId: string | undefined

  constructor() {
    makeObservable(this, {
      forceUpdateBookingPaymentsCounter: observable,
      forceUpdateBookingCreditCardsCounter: observable,
      paymentId: observable,
      bookingPayments: computed,
      forceUpdateBookingPayments: action.bound,
      forceUpdateBookingCreditCards: action.bound,
      setPaymentId: action.bound,
      availablePaymentCards: computed,
      availablePaymentTerminals: computed,
      availablePaymentBanks: computed,
      wholesalePaymentOptions: computed,
      isNewChargeRequestPending: computed,
    })

    this.retryPaymentOnTerminal = trackedAction<any>(this.retryPaymentOnTerminal.bind(this))
    this.enterNewCardForDeferredBookingPayment = trackedAction<any>(
      this.enterNewCardForDeferredBookingPayment.bind(this),
    )
    this.saveNewCreditCardFromTerminal = trackedAction<any>(
      this.saveNewCreditCardFromTerminal.bind(this),
    )
    this.addNewCreditCard = trackedAction<any>(this.addNewCreditCard.bind(this))
    this.deleteBookingCharge = trackedAction<any>(this.deleteBookingCharge.bind(this))
    this.setDeferredPaymentAmount = trackedAction<any>(this.setDeferredPaymentAmount.bind(this))
    this.makeAchBankCharge = trackedAction<any>(this.makeAchBankCharge.bind(this))
    this.makeWholesaleCharge = trackedAction<any>(this.makeWholesaleCharge.bind(this))
    this.makeWalletCharge = trackedAction<any>(this.makeWalletCharge.bind(this))
    this.makeSecurityDepositCharge = trackedAction<any>(this.makeSecurityDepositCharge.bind(this))
    this.makeFolioSecurityDepositCharge = trackedAction<any>(
      this.makeFolioSecurityDepositCharge.bind(this),
    )
    this.makePaymentLinkCharge = trackedAction<any>(this.makePaymentLinkCharge.bind(this))
    this.makeBookingCardCharge = trackedAction<any>(this.makeBookingCardCharge.bind(this))
    this.makeNewCardCharge = trackedAction<any>(this.makeNewCardCharge.bind(this))
    this.cancelPaymentIfAwaiting = trackedAction<any>(this.cancelPaymentIfAwaiting.bind(this))
  }

  /**
   * .list inside is always sorted from most recent update to older
   */

  get bookingPayments(): Promise<BookingPaymentView[]> {
    void [this.forceUpdateBookingPaymentsCounter]
    dependsOn(
      BookingStore.booking,
      RefundStore.refundPayments,
      BookingValueAddedServicesStore.adjustRequestedService,
      BookingValueAddedServicesStore.makeCharge,
      BookingAdjustmentStore.refundPaymentMethods,
      this.cancelPaymentIfAwaiting,
      this.makeNewCardCharge,
      this.makeBookingCardCharge,
      this.makePaymentLinkCharge,
      this.makeSecurityDepositCharge,
      this.makeFolioSecurityDepositCharge,
      this.makeWholesaleCharge,
      this.makeWalletCharge,
      this.deleteBookingCharge,
    )

    return BookingStore.booking
      .then(booking => {
        if (!booking?.id) return Promise.resolve([])

        return getBookingPayments(booking.id)
      })
      .then(bookingPayments => sortPaymentsByRecentActivity(bookingPayments))
  }

  forceUpdateBookingPayments() {
    this.forceUpdateBookingPaymentsCounter += 1
  }

  forceUpdateBookingCreditCards() {
    this.forceUpdateBookingCreditCardsCounter += 1
  }

  setPaymentId(paymentId?: string) {
    this.paymentId = paymentId
  }

  get availablePaymentCards() {
    void [this.forceUpdateBookingCreditCardsCounter]
    dependsOn(BookingStore.booking)

    return BookingStore.booking.then(booking => {
      if (booking?.id) {
        return getAvailableBookingPaymentCards(booking.id)
      }

      return Promise.resolve(undefined)
    })
  }

  get availablePaymentTerminals() {
    dependsOn(BookingStore.booking)

    return BookingStore.booking.then(booking => {
      if (booking?.homeId) {
        return getAvailablePaymentTerminals(booking?.homeId)
      }

      return Promise.resolve(undefined)
    })
  }

  get availablePaymentBanks() {
    dependsOn(BookingStore.booking)

    return BookingStore.booking.then(booking => {
      if (!booking?.id) return Promise.resolve(undefined)

      return getAvailablePaymentBanks(booking.id)
    })
  }

  get wholesalePaymentOptions() {
    return bookings.getWholesalePaymentOptions()
  }

  get isNewChargeRequestPending() {
    return (
      isPending(this.makeNewCardCharge) ||
      isPending(this.makeBookingCardCharge) ||
      isPending(this.makePaymentLinkCharge) ||
      isPending(this.makeSecurityDepositCharge) ||
      isPending(this.makeFolioSecurityDepositCharge) ||
      isPending(this.makeWholesaleCharge)
    )
  }

  // Actions

  async cancelPaymentIfAwaiting(paymentId: string) {
    return folio
      .cancelPaymentIfAwaiting(paymentId)
      .finally(() => BookingStore.updateBookingValuesFromServer())
  }

  async makeNewCardCharge({
    bookingId,
    amount,
    title,
  }: {
    bookingId: string
    amount: number
    title: string
  }) {
    return arriere.bookings
      .makeNewCardCharge({
        bookingId,
        amount,
        title,
      })
      .finally(() => BookingStore.updateBookingValuesFromServer())
  }

  async makeBookingCardCharge({
    bookingId,
    amount,
    title,
    cardId,
  }: {
    bookingId: string
    amount: number
    title: string
    cardId: string
  }) {
    return arriere.bookings
      .makeBookingCardCharge({
        bookingId,
        amount,
        title,
        cardId,
      })
      .finally(() => BookingStore.updateBookingValuesFromServer())
  }

  async makePaymentLinkCharge({
    bookingId,
    amount,
    title,
    email,
  }: {
    bookingId: string
    amount: number
    title: string
    email: string
  }) {
    return arriere.bookings
      .makePaymentLinkCharge({
        amount,
        bookingId,
        title,
        email,
      })
      .then(response => response.makePaymentLinkCharge)
      .finally(() => BookingStore.updateBookingValuesFromServer())
  }

  async makeFolioSecurityDepositCharge({
    depositId,
    amount,
  }: {
    depositId: string
    amount: number
  }) {
    return BookingStore.booking
      .then(booking => {
        if (!booking?.id) return Promise.reject('Booking ID is required')

        return folio.chargeDeposit(booking.id, depositId, amount)
      })
      .finally(() => BookingStore.updateBookingValuesFromServer())
  }

  async makeSecurityDepositCharge({
    bookingId,
    amount,
    title,
  }: {
    bookingId: string
    amount: number
    title: string
  }) {
    return arriere.bookings
      .makeSecurityDepositCharge({
        amount,
        bookingId,
        title,
      })
      .finally(() => BookingStore.updateBookingValuesFromServer())
  }

  async makeWalletCharge({ email, amount }: { email: string; amount: number }) {
    return BookingStore.booking.then(booking => {
      if (!booking?.id) return Promise.reject('Booking ID is required')

      return folio
        .chargeWallet(booking.id, email, amount)
        .finally(() => BookingStore.updateBookingValuesFromServer())
    })
  }

  async makeWholesaleCharge({
    bookingId,
    amount,
    title,
    platform,
    time,
  }: {
    bookingId: string
    amount: number
    title: string
    platform: string
    time: string
  }) {
    return arriere.bookings
      .makeWholesaleCharge({
        bookingId,
        amount,
        title,
        platform,
        time,
      })
      .finally(() => BookingStore.updateBookingValuesFromServer())
  }

  async makeAchBankCharge({
    customerId,
    bookingId,
    amount,
    title,
  }: {
    customerId: string
    bookingId: string
    amount: number
    title: string
  }) {
    return arriere.bookings.makeAchBankCharge({
      customerId,
      bookingId,
      amount,
      title,
    })
  }

  async deleteBookingCharge(paymentId: string) {
    trackEvent('Booking direct payment: Attempting to remove booking charge.', {
      data: { paymentId },
    })

    try {
      await arriere.bookings
        .deleteBookingCharge({
          paymentId,
        })
        .then(
          () => {
            Alert.Success(`Success! The transaction was removed.`)
            BookingStore.updateBookingValuesFromServer()
            trackEvent('Booking direct payment: Removed booking charge successfully!', {
              data: { paymentId },
            })
          },
          error => {
            trackError('Booking direct payment: Error deleting booking charge.', {
              error,
            })

            Alert.Error(`Oops! We were not able to remove this transaction.`)
          },
        )
    } catch (error) {
      trackError('Booking direct payment: Error deleting booking charge.', { error })

      Alert.Error(`Oops! We were not able to process your request.`)
    }
  }

  async setDeferredPaymentAmount({ paymentId, amount }: SetDeferredBookingPaymentAmountProps) {
    trackEvent('Booking deferred payment: Attempting to change deferred payment amount.', {
      data: { paymentId, amount },
    })

    try {
      const isAmountUpdated = await setDeferredBookingPaymentAmount({ paymentId, amount })

      if (!isAmountUpdated) {
        throw new Error('The setDeferredBookingPaymentAmount mutation returned false.')
      }

      Alert.Success(`Success! The transaction amount was updated.`)
      trackEvent('Booking deferred payment: Deferred payment amount changed successfully!', {
        data: { paymentId, amount },
      })
      BookingStore.updateBookingValuesFromServer()
    } catch (error) {
      trackError('Booking deferred payment: Error changing deferred payment amount.', { error })

      Alert.Error(`Oops! We were not able to update this transaction amount.`)
    }
  }

  async addNewCreditCard(paymentMethod: string) {
    const bookingId = await BookingStore.booking.then(booking => booking?.id)

    if (!paymentMethod) {
      return Alert.Error(`Missing payment method from Stripe`)
    }

    trackEvent('Add New Credit Card to Folio: Attempting to create new credit card.', {
      data: { bookingId },
    })

    try {
      if (!bookingId) {
        throw new Error('Booking ID is required to add a new credit card.')
      }

      const { data, errors } = await arriereBackoffice.mutations.payments.raw({
        __alias: 'addNewCreditCard',
        addCreditCard: {
          __args: { paymentMethod, bookingId },
        },
      })

      if (errors) {
        throw new Error(errors[errors.length - 1].message)
      }

      return data?.addCreditCard
    } catch (error) {
      trackError('Add New Credit Card to Folio', {
        error,
      })

      return Alert.Error(`Oops! We were not able to process your request. ${error}`)
    }
  }

  async saveNewCreditCardFromTerminal(readerId: string) {
    const bookingId = await BookingStore.booking.then(booking => booking?.id)

    trackEvent('Booking save credit card: Attempting to save credit card from terminal.', {
      data: { bookingId, readerId },
    })

    try {
      if (!bookingId) {
        throw new Error('Booking ID is required to save a new credit card from terminal.')
      }

      await saveCreditCardFromTerminal({ bookingId, readerId })
    } catch (error) {
      trackError('Booking save credit card: Error saving credit card from terminal', { error })

      Alert.Error(`Oops! We were not able to process your request.`)
    }
  }

  async removeSavedCreditCard(creditCardId: string) {
    try {
      await deleteSavedCreditCard(creditCardId)

      Alert.Success(`Success! Credit card was deleted.`)
      trackEvent(
        'Booking delete credit card: Credit card was successfully removed from the booking!',
        {
          data: { creditCardId },
        },
      )

      this.forceUpdateBookingCreditCards()
      return true
    } catch (error) {
      trackError('Add New Credit Card to Folio: Error releasing security deposit.', {
        error,
      })

      trackError('Booking delete credit card: Error deleting credit card from booking!', {
        data: { creditCardId },
      })

      return Alert.Error(`${error}`)
    }
  }

  async enterNewCardForDeferredBookingPayment({
    paymentId,
    paymentMethod,
  }: CompleteDeferredBookingPaymentProps) {
    try {
      const result = await completeDeferredBookingPayment({ paymentId, paymentMethod })
      if (result.completeDeferredBookingPayment !== 'OK') {
        throw new Error('We could not confirm that this payment was collected.')
      }
      return true
    } catch (error) {
      handleStoreError({
        action: 'Deferred booking payment collection failed',
        error,
        context: { paymentId },
        userMessage: 'We could not collect this payment. Please try again.',
      })
      return false
    } finally {
      this.forceUpdateBookingPayments()
      this.forceUpdateBookingCreditCards()
    }
  }

  async retryPaymentOnTerminal({ paymentId, readerId }: RetryPaymentProps) {
    trackEvent('Booking retry payment: Attempting to retry payment.', {
      data: { paymentId, readerId },
    })

    try {
      return await retryPaymentOnPaymentTerminal({ paymentId, readerId }).then(res => {
        return res
      })
    } catch (error) {
      trackError('Booking retry payment: Error retrying payment', {
        error,
      })

      Alert.Error(`Oops! We were not able to process your request.`)
    }
  }
}

export const BookingPaymentStore = new _BookingPaymentStore()
